/**
 * ========================================
 * DEMO REQUEST TYPES
 * ========================================
 * Domain types for the "Book a Live Demo" module. Fields match the
 * backend DemoRequestDTO exactly (snake_case), same convention as
 * ticket.ts / doubtSession.ts / calendar.ts.
 */

export type Standard = '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'ALL';

/** "1" -> "1st", "2" -> "2nd", "3" -> "3rd", "4".."10" -> "4th".."10th", "ALL" -> "1st – 10th"
 * (school-wide booking, not one grade -- see BookDemoModal's `isSchool` mode) -- the label
 * convention for this module's Standard field everywhere it's shown (booking form,
 * admin list/detail/assign/schedule/edit modals). */
export const standardOrdinal = (s: Standard | string): string => {
  if (s === 'ALL') return '1st – 10th';
  const v = Number(s);
  if (v === 1) return '1st';
  if (v === 2) return '2nd';
  if (v === 3) return '3rd';
  return `${v}th`;
};
export type Board = 'CBSE' | 'ICSE' | 'State Board' | 'Other';
export type DemoStatus =
  | 'NEW' | 'ASSIGNED' | 'DEMO_SCHEDULED' | 'DEMO' | 'FOLLOW_UP'
  | 'ENROLLED' | 'NOT_ENROLLED' | 'CANCELLED' | 'CLOSED';
export type FinalResult =
  | 'ENROLLED' | 'INTERESTED' | 'FOLLOW_UP_REQUIRED' | 'NOT_INTERESTED'
  | 'NOT_ELIGIBLE' | 'CANCELLED' | 'NO_SHOW' | 'CLOSED';

export interface TeacherSummary {
  id: string;
  first_name: string;
  last_name?: string | null;
  email: string;
}

export interface DemoRequest {
  id: string;
  email: string;
  phone: string;
  standard: string;
  board: string;
  state?: string | null;
  preferred_date: string;
  preferred_time: string;
  timezone?: string | null;
  status: DemoStatus;
  assigned_teacher?: TeacherSummary | null;
  assigned_at?: string | null;
  assigned_by?: string | null;
  demo_scheduled_at?: string | null;
  last_followup_at?: string | null;
  next_followup_at?: string | null;
  final_result?: FinalResult | null;
  final_notes?: string | null;
  notes?: string | null;
  completed_at?: string | null;
  cancelled_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface DemoRequestPublic {
  id: string;
  status: DemoStatus;
  duplicate: boolean;
}

export interface DemoRequestCreateRequest {
  email: string;
  phone: string;
  standard: Standard;
  board: Board;
  state?: string;
  preferred_date: string;
  preferred_time: string;
  timezone?: string;
}

export interface DemoRequestUpdateRequest {
  email?: string;
  phone?: string;
  standard?: Standard;
  board?: Board;
  state?: string;
  preferred_date?: string;
  preferred_time?: string;
  timezone?: string;
  notes?: string;
}

export interface DemoRequestFilters {
  search?: string;
  status?: DemoStatus;
  board?: Board;
  standard?: Standard;
  assigned_teacher_id?: string;
  final_result?: FinalResult;
  preferred_date_from?: string;
  preferred_date_to?: string;
}

export interface AssignTeacherRequest {
  teacher_id: string;
}

export interface StatusUpdateRequest {
  status: DemoStatus;
  notes?: string;
}

export interface ScheduleRequest {
  demo_scheduled_at: string;
  notes?: string;
}

export interface FollowUpRequest {
  notes: string;
  next_followup_at?: string;
}

export interface FinalResultRequest {
  result: FinalResult;
  notes?: string;
  next_followup_at?: string;
}

export interface DemoRequestAuditLogEntry {
  id: string;
  action: string;
  field?: string | null;
  old_value?: string | null;
  new_value?: string | null;
  notes?: string | null;
  performed_by?: TeacherSummary | null;
  created_at?: string | null;
}

export interface DemoRequestSummary {
  new: number;
  assigned: number;
  scheduled: number;
  todays_demos: number;
  follow_up: number;
  enrolled: number;
  not_enrolled: number;
}

export const STANDARD_OPTIONS: Standard[] = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];
export const BOARD_OPTIONS: Board[] = ['CBSE', 'ICSE', 'State Board', 'Other'];

export const STATUS_OPTIONS: DemoStatus[] = [
  'NEW', 'ASSIGNED', 'DEMO_SCHEDULED', 'DEMO', 'FOLLOW_UP',
  'ENROLLED', 'NOT_ENROLLED', 'CANCELLED', 'CLOSED',
];

export const FINAL_RESULT_OPTIONS: FinalResult[] = [
  'ENROLLED', 'INTERESTED', 'FOLLOW_UP_REQUIRED', 'NOT_INTERESTED',
  'NOT_ELIGIBLE', 'CANCELLED', 'NO_SHOW', 'CLOSED',
];

export const STATUS_LABEL: Record<DemoStatus, string> = {
  NEW: 'New',
  ASSIGNED: 'Assigned',
  DEMO_SCHEDULED: 'Demo Scheduled',
  DEMO: 'Demo',
  FOLLOW_UP: 'Follow-up',
  ENROLLED: 'Enrolled',
  NOT_ENROLLED: 'Not Enrolled',
  CANCELLED: 'Cancelled',
  CLOSED: 'Closed',
};

export const STATUS_CHIP: Record<DemoStatus, string> = {
  NEW: 'bg-amber-100 text-amber-700',
  ASSIGNED: 'bg-indigo-100 text-indigo-700',
  DEMO_SCHEDULED: 'bg-purple-100 text-purple-700',
  DEMO: 'bg-blue-100 text-blue-700',
  FOLLOW_UP: 'bg-cyan-100 text-cyan-700',
  ENROLLED: 'bg-green-100 text-green-700',
  NOT_ENROLLED: 'bg-gray-100 text-gray-700',
  CANCELLED: 'bg-red-100 text-red-700',
  CLOSED: 'bg-gray-100 text-gray-500',
};

export const RESULT_LABEL: Record<FinalResult, string> = {
  ENROLLED: 'Enrolled',
  INTERESTED: 'Interested',
  FOLLOW_UP_REQUIRED: 'Follow-up Required',
  NOT_INTERESTED: 'Not Interested',
  NOT_ELIGIBLE: 'Not Eligible',
  CANCELLED: 'Cancelled',
  NO_SHOW: 'No Show',
  CLOSED: 'Closed',
};

export const RESULT_CHIP: Record<FinalResult, string> = {
  ENROLLED: 'bg-green-100 text-green-700',
  INTERESTED: 'bg-blue-100 text-blue-700',
  FOLLOW_UP_REQUIRED: 'bg-cyan-100 text-cyan-700',
  NOT_INTERESTED: 'bg-gray-100 text-gray-700',
  NOT_ELIGIBLE: 'bg-gray-100 text-gray-500',
  CANCELLED: 'bg-red-100 text-red-700',
  NO_SHOW: 'bg-orange-100 text-orange-700',
  CLOSED: 'bg-gray-100 text-gray-500',
};

// No master-data/config mechanism exists in this codebase for Indian states
// (confirmed by search — RegisterPage.tsx's `state` field is a free-text
// input). This is a plain constant list, following the app's existing
// hardcoded-options convention (see STANDARD_OPTIONS/BOARD_OPTIONS above).
export const INDIAN_STATES: string[] = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa',
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala',
  'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland',
  'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Jammu and Kashmir',
  'Ladakh', 'Lakshadweep', 'Puducherry',
];
